import { NUMBER_MAX_DECIMAL_PLACES } from '../Number/NumberInput.types';
import { getFormattedValue as getNumberFormattedValue } from '../Number/utils';

export const isValidCurrency = (currency: string) => {
  try {
    // eslint-disable-next-line no-new
    new Intl.NumberFormat(undefined, { style: 'currency', currency });
    return true;
  } catch {
    return false;
  }
};

export const getFormattedValue = (
  value: string,
  locale: string,
  currency?: string,
  {
    showGroupSeparators = true,
    showAs = 'code',
    numberOfDecimals
  }: { showGroupSeparators?: boolean; showAs?: 'code' | 'symbol'; numberOfDecimals?: number } = {}
) => {
  const [integer, fractionPart] = value.split('.', 2) as [string, string?];

  if (!currency)
    return getNumberFormattedValue(value, locale, undefined, {
      useGrouping: showGroupSeparators,
      maximumFractionDigits: numberOfDecimals,
      minimumFractionDigits: Math.min(numberOfDecimals ?? 0, fractionPart?.length ?? 0)
    });

  const isCurrency = isValidCurrency(currency);

  const formatter = new Intl.NumberFormat(locale, {
    useGrouping: showGroupSeparators,
    maximumFractionDigits: numberOfDecimals,
    minimumFractionDigits: Math.min(numberOfDecimals ?? 0, fractionPart?.length ?? 0),
    ...(isCurrency
      ? {
          style: 'currency',
          currency,
          currencyDisplay: showAs
        }
      : {}),
    numberingSystem: 'latn'
  });

  let formatted = value;

  if (value && !Number.isNaN(parseFloat(integer))) {
    const endsWithDot = fractionPart === '';
    const valueNumber = endsWithDot ? `${value}1` : value;
    formatted = formatter
      .formatToParts(valueNumber as `${number}`)
      .reduce((result, { type, value: part }) => {
        if (type === 'fraction' && endsWithDot) return result;
        return result + part;
      }, '');
  } else if (currency) {
    formatted = formatter
      .formatToParts(value.startsWith('-') ? -1 : 1)
      .reduce(
        (result, { type, value: part }) =>
          ['currency', 'minusSign'].includes(type) ? result + part : result,
        ''
      );
  }
  return formatted;
};

export function getCurrencyMinimumFractionDigits(locale: string, currencyISOCode?: string): number {
  return currencyISOCode
    ? new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyISOCode
      }).resolvedOptions().minimumFractionDigits ?? 0
    : NUMBER_MAX_DECIMAL_PLACES;
}

interface CurrencyFormatOptions {
  locale: string;
  options?: Intl.NumberFormatOptions;
}

export function formatCurrency(value: number | bigint, { locale, options }: CurrencyFormatOptions) {
  const formatter = new Intl.NumberFormat(locale, options);

  return formatter.format(value);
}
